/**
 * Market Categories: domain types, contract and error class.
 *
 * The runtime implementation lives in `httpMarketCategoriesService.ts` and
 * is wired into `MarketCategoriesProvider`. Tests can supply their own
 * implementation of `MarketCategoriesService` via the Provider's `service`
 * prop without depending on this file beyond the type exports.
 */

import type { TierKey } from '../constants/tiers';

export type TierThresholds = Record<TierKey, number>;

export interface PendingMarketCategoryInfo {
  operation: 'create' | 'update' | 'rename' | 'delete';
  createdAt: string;
  createdBy: string;
  /**
   * For `rename` pendings: the name the category had before the rename.
   * The current `MarketCategory.name` reflects the post-rename name.
   */
  previousName?: string;
}

/**
 * Metadata describing an uncommitted threshold edit staged in Cosmos. The
 * `MarketCategory.thresholds` field already reflects the pending payload (so
 * inputs and the export are consistent with what will be committed); this
 * record exists so the UI can highlight the cells that changed and show
 * what the previous value was.
 */
export interface PendingThresholdsInfo {
  operation: 'update';
  createdAt: string;
  createdBy: string;
  previousThresholds: TierThresholds;
}

/**
 * Per-country breakdown of what would change once a pending Update is applied
 * to the SQL store. Computed by diffing the pending payload against the
 * committed SQL state. Absent when nothing would change (or when the category
 * has no pending change at all).
 */
export interface PendingCountryDiff {
  added: string[];   // in pending payload but not yet committed
  removed: string[]; // committed but no longer in pending payload
}

/**
 * Metadata describing the most-recently applied change for a given scope, as
 * surfaced by `GET /api/ProgramConfig/lastApplied/...`. Used by the UI to
 * render "Last updated: {when} by {who}" so reviewers can tell this month's
 * approved data from stale data.
 */
export interface LastUpdatedInfo {
  /** ISO-8601 timestamp of when the change was committed to SRA SQL. */
  at: string;
  /** UPN of the user who staged the change. */
  by: string;
}

export interface MarketCategory {
  id: string;
  name: string;
  countries: string[];
  thresholds: TierThresholds;
  /**
   * When present, indicates this category has an uncommitted change staged in
   * Cosmos that has not yet been applied to the SQL store. The merged countries
   * already reflect the pending payload; this field exists purely so the UI can
   * render a badge / tooltip. Absent for rows that are fully committed.
   */
  pending?: PendingMarketCategoryInfo;
  /**
   * Per-country diff against the committed SQL state. Set together with
   * `pending` whenever the pending payload differs from the committed list.
   * The `added` countries are members of `countries`; the `removed` countries
   * are NOT (they were removed by the pending update) and need to be rendered
   * separately as struck-through pills.
   */
  pendingCountryDiff?: PendingCountryDiff;
  pendingThresholds?: PendingThresholdsInfo;
  /**
   * Most-recent Applied change to the category's country list (the
   * MarketCategories resource). Drives the "Last updated" column on the
   * Market Categories tab. Absent when the row has never had an applied
   * change tracked through Cosmos (legacy SQL-only data, or post-rename).
   */
  lastUpdatedCategory?: LastUpdatedInfo;
  /**
   * Most-recent Applied change to the category's tier thresholds (the
   * ThresholdsByMarket resource). Drives the "Last updated" column on the
   * Thresholds by Market tab.
   */
  lastUpdatedThresholds?: LastUpdatedInfo;
}

export const ZERO_THRESHOLDS: TierThresholds = {
  silver: 0,
  gold: 0,
  platinum: 0,
};

export class MarketCategoriesError extends Error {
  code: 'DUPLICATE_NAME' | 'EMPTY_NAME' | 'EMPTY_COUNTRIES' | 'NOT_FOUND' | 'DUPLICATE_COUNTRY';
  constructor(code: MarketCategoriesError['code'], message: string) {
    super(message);
    this.code = code;
    this.name = 'MarketCategoriesError';
  }
}

export interface MarketCategoriesService {
  list(): Promise<MarketCategory[]>;
  create(input: { name: string }): Promise<MarketCategory>;
  updateName(id: string, name: string): Promise<MarketCategory>;
  updateCountries(id: string, countries: string[]): Promise<MarketCategory>;
  updateThresholds(id: string, thresholds: TierThresholds): Promise<MarketCategory>;
  remove(id: string): Promise<void>;
  /**
   * Cancels any uncommitted (Pending) change staged in Cosmos for the given
   * category. Used by the UI to undo a pending Delete (and pending
   * Rename/Update) without staging another change. Idempotent: safe to call
   * even when nothing is pending. After calling, callers should refresh the
   * list so the merged view reflects the cancellation.
   */
  cancelPending(id: string): Promise<void>;
  /**
   * Cancels any uncommitted threshold change staged in Cosmos for the given
   * category. Used by the UI to undo a pending tier-threshold edit without
   * staging a new value. Idempotent.
   */
  cancelPendingThresholds(id: string): Promise<void>;
}
